import { ProfileData } from '../services/profileService';
import { getProfilePictureUrl } from '../services/profileService';

/**
 * Profile Utility Functions
 * 
 * These utilities provide common profile-related operations that can be used
 * across multiple pages and components. The profile data is stored in DynamoDB
 * and accessed via the ProfileContext or profileService.
 */

/**
 * Get the user's avatar URL for rendering.
 *
 * Resolution order:
 *   1. `profile.avatarUrl` — short-lived presigned GET URL emitted by
 *      the native M1 `GET /profile` (and `PUT /profile`) handler.
 *      Preferred when present: the bucket is private (BlockPublicAccess
 *      on) so this is the only URL the browser can actually fetch.
 *   2. `profile.avatar` if it already looks like a fully-qualified URL
 *      (legacy Auth0 `picture` claims, etc.).
 *   3. Built directly from `profile.avatar` as an S3 key against the
 *      profile-pictures bucket — left only as a backward-compat path
 *      for any stack that hasn't yet deployed the presign helper.
 *      Will 403 against a private bucket; intentional fallback only.
 *
 * @param profile - The user's profile data
 * @param stage - The environment stage (staging or production)
 * @returns A browser-renderable URL or null if no avatar
 */
export function getAvatarUrl(profile: ProfileData | null, stage: 'staging' | 'production'): string | null {
  if (!profile) {
    return null;
  }

  if (profile.avatarUrl) {
    return profile.avatarUrl;
  }

  if (!profile.avatar) {
    return null;
  }

  if (profile.avatar.startsWith('http')) {
    return profile.avatar;
  }

  return getProfilePictureUrl(profile.avatar, stage);
}

/**
 * Get the user's display name
 * 
 * Priority: firstName + lastName > firstName > lastName > email > "User"
 * 
 * @param profile - The user's profile data
 * @returns The display name string
 */
export function getDisplayName(profile: ProfileData | null): string {
  if (!profile) {
    return 'User';
  }
  
  const firstName = (profile.firstName || '').trim();
  const lastName = (profile.lastName || '').trim();
  const email = profile.email || '';
  
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (firstName) {
    return firstName;
  }
  if (lastName) {
    return lastName;
  }
  if (email) {
    return email;
  }
  
  return 'User';
}

/**
 * Get the user's initials for avatar fallback
 * 
 * Priority:
 * 1. firstName + lastName → first letter of each (e.g., "PK" for "Phill Kross")
 * 2. firstName only → first TWO letters of firstName (e.g., "PH" for "Phill")
 * 3. lastName only → first letter of lastName
 * 4. email-based initials → fallback logic
 * 5. "U" → default
 * 
 * @param profile - The user's profile data
 * @returns The initials string (1-2 characters)
 */
export function getInitials(profile: ProfileData | null): string {
  if (!profile) {
    return 'U';
  }
  
  const firstName = (profile.firstName || '').trim();
  const lastName = (profile.lastName || '').trim();
  const email = profile.email || '';
  
  // Debug: Log what we're working with
  console.log('[getInitials] Profile data:', {
    firstName,
    lastName,
    email,
    hasFirstName: !!firstName,
    hasLastName: !!lastName,
    firstNameLength: firstName.length,
    lastNameLength: lastName.length
  });
  
  // Priority 1: Use first letter of firstName + first letter of lastName if both exist
  if (firstName && lastName) {
    const result = `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
    console.log('[getInitials] Returning from firstName + lastName:', result);
    return result;
  }
  
  // Priority 2: Use first TWO letters of firstName if only firstName exists
  if (firstName && firstName.length >= 2) {
    return firstName.substring(0, 2).toUpperCase();
  }
  
  // Priority 3: Use first letter of firstName if it's only 1 character
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  
  // Priority 4: Use last name initial if available
  if (lastName) {
    return lastName.charAt(0).toUpperCase();
  }
  
  // Priority 5: Fall back to email-based initials
  if (email) {
    const emailPrefix = email.split('@')[0];
    const krossIndex = emailPrefix.toLowerCase().indexOf('kross');
    
    // Special handling for "kross" in email (e.g., "phillkross" -> "PK")
    if (krossIndex > 0) {
      return `${emailPrefix.charAt(0).toUpperCase()}K`;
    }
    
    // Use first 2 characters of email prefix
    if (emailPrefix.length >= 2) {
      return emailPrefix.substring(0, 2).toUpperCase();
    }
    
    // Use first character of email
    return email.charAt(0).toUpperCase();
  }
  
  return 'U';
}

/**
 * Get the user's full name (firstName + lastName)
 * 
 * @param profile - The user's profile data
 * @returns The full name string or empty string
 */
export function getFullName(profile: ProfileData | null): string {
  if (!profile) {
    return '';
  }
  
  const firstName = (profile.firstName || '').trim();
  const lastName = (profile.lastName || '').trim();
  
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (firstName) {
    return firstName;
  }
  if (lastName) {
    return lastName;
  }
  
  return '';
}

/**
 * Get the user's email address
 * 
 * @param profile - The user's profile data
 * @returns The email address or empty string
 */
export function getEmail(profile: ProfileData | null): string {
  return profile?.email || '';
}

/**
 * Get the user's phone number
 * 
 * @param profile - The user's profile data
 * @returns The phone number or empty string
 */
export function getPhone(profile: ProfileData | null): string {
  return profile?.phone || '';
}

/**
 * Get the user's role
 * 
 * @param profile - The user's profile data
 * @returns The role or empty string
 */
export function getRole(profile: ProfileData | null): string {
  return profile?.role || '';
}

/**
 * Check if the profile has complete information
 * 
 * @param profile - The user's profile data
 * @returns True if profile has firstName, lastName, and email
 */
export function isProfileComplete(profile: ProfileData | null): boolean {
  if (!profile) {
    return false;
  }
  
  const firstName = (profile.firstName || '').trim();
  const lastName = (profile.lastName || '').trim();
  const email = (profile.email || '').trim();
  
  return firstName.length > 0 && lastName.length > 0 && email.length > 0;
}

